import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { ANALYSIS_TERMINAL_STATUSES, AnalysisStatus } from '@/features/workspaces/constants';
import { parseStreamRow, queryStream, type StreamRowPayload } from '@/features/workspaces/stream';
import type { ChangeAssessment } from '@/features/supplements/types';
import { matchesChangeAssessmentSnapshot } from '@/features/supplements/utils';

export const CHANGE_ASSESSMENT_LIST_QUERY_KEY = 'changeAssessmentList';
const CHANGE_ASSESSMENT_QUERY_KEY = 'changeAssessment';

export const changeAssessmentListQuery = (supplementId: string, workspaceId: string) => {
  const queryKey = [CHANGE_ASSESSMENT_LIST_QUERY_KEY, supplementId];
  const invalidate = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: [CHANGE_ASSESSMENT_QUERY_KEY, supplementId] });
  };
  return queryOptions({
    queryKey,
    queryFn: async () =>
      (
        await api.get<ChangeAssessment[]>('v1/change-assessments', {
          params: { type: DOCUMENT_TYPE.supplement, resourceId: supplementId },
        })
      ).data,
    meta: {
      stream: queryStream<ChangeAssessment[], StreamRowPayload<AnalysisStatus>>({
        topic: 'change_assessment',
        type: DOCUMENT_TYPE.contract,
        resourceId: workspaceId,
        isActive: (data) =>
          data?.some((item) => item.status === AnalysisStatus.IN_PROGRESS) ?? false,
        onStatusUpdate: (payload, queryClient) => {
          const current = queryClient.getQueryData<ChangeAssessment[]>(queryKey);
          if ('rows' in payload && matchesChangeAssessmentSnapshot(payload.rows, current)) return;

          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate(queryClient);
          if (!row) return;

          const known = current?.some((item) => item.id === row.rowId);
          if (!known || ANALYSIS_TERMINAL_STATUSES.includes(row.status))
            return invalidate(queryClient);

          queryClient.setQueryData<ChangeAssessment[]>(queryKey, (old) =>
            old?.map((item) => (item.id === row.rowId ? { ...item, status: row.status } : item)),
          );
        },
      }),
    },
  });
};

export const changeAssessmentQuery = (
  contractSupplementPackageId: string,
  changeAssessmentId: string,
) =>
  queryOptions({
    queryKey: [CHANGE_ASSESSMENT_QUERY_KEY, contractSupplementPackageId, changeAssessmentId],
    queryFn: async () =>
      (
        await api.get<ChangeAssessment>(`v1/change-assessments/${changeAssessmentId}`, {
          params: { type: DOCUMENT_TYPE.supplement, resourceId: contractSupplementPackageId },
        })
      ).data,
  });
